// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.RoleSetupStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { action, autorun, computed, observable } from 'mobx';
import fetcher from 'fetchers/Izenda.RoleSetupFetcher';
import tenantManagementDataFetcher from 'fetchers/Izenda.TenantManagementDataFetcher';
import uiState from 'logic/Izenda.UiState';
import commonMessages from 'share/messages/Izenda.CommonMessages';
import RoleList from './models/Izenda.RoleList';
import RoleDetail from './models/Izenda.RoleDetail';
import DataModelAccess from './models/Izenda.RoleDataModelAccess';
import accessFetcher from 'fetchers/Izenda.AccessDataFetcher';
import UsersRoleSetupStore from 'logic/Izenda.UsersRoleSetupStore';
import PermissionStore from './Izenda.PermissionStore';
import PermissionDetail from './models/Izenda.PermissionDetail';
import { DATA_SOURCE_CONCAT_TREE_MAP, DATA_SOURCE_TREE_MAP, ROLE_TYPES } from 'logic/data/Izenda.RoleSetupConstants';
import AccessStore from 'logic/Izenda.PermissionAccessStore';
import PermissionSummaryStore from 'logic/Izenda.PermissionSummaryStore';
import localizer from 'share/localization/Izenda.Localizer';
import { find, forEach, get, getOr, head, invoke, isEmpty, isNil, map } from 'lodash/fp';
import { concatTree, parseToTree, updateInteractiveTree, updateTree } from 'share/utils/Izenda.LazyLoadingUtils';
import SecurityManager from 'share/authorization/Izenda.SecurityManager';
import { PERMISSION_KEY } from 'logic/data/Izenda.AuthorizationConstants';

const IS_TENANT_SETUP = false;

class RoleSetupStore {
  @observable roleList;
  @observable roleDetail;
  @observable alertMessage;

  constructor() {
    this.roleList = new RoleList();
    this.roleDetail = new RoleDetail();
    this.alertMessage = {};
    this.accessStore = new AccessStore();
    this.usersRoleSetupStore = new UsersRoleSetupStore();
    this.permissionStore = new PermissionStore(this.roleDetail.permission, this.tenantId, IS_TENANT_SETUP);
    this.permissionSummaryStore = new PermissionSummaryStore();
    this.dataModelAccess = new DataModelAccess(this.roleDetail);

    this.lazyLoadingStore = {};
    this.loadPartialDataModelAccess = null;
    this.disposeAutoUpdateTenantId = autorun(() => {
      this.permissionStore && (this.permissionStore.tenantId = this.tenantId);
    });
  }

  @computed
  get isDirty() {
    return (
      this.roleDetail.hasChange ||
      this.usersRoleSetupStore.model.isDirty ||
      this.permissionStore.model.isDirty ||
      this.dataModelAccess.hasChange
    );
  }

  @computed
  get tenantId() {
    return uiState.getTenantId();
  }

  /**
   * initial for role setup store
   * @method init
   */
  init() {
    // whenever uiState changed the tenant.
    uiState.setCheckDirtyFunction(() => this.isDirty);
    this.cancelLoadDataModelAccess();
  }

  /**
   * destroy observe
   */
  destroy() {
    uiState.resetCheckDirtyFunction();
    this.disposeAutoUpdateTenantId();
  }

  /**
   * Load Data role setup tenant and role list
   * @return {Promise}
   */
  loadDataRoleSetup() {
    return Promise.all([this.loadTenant(), this.getRoleTemplates(), this.getRoleList()]);
  }

  /**
   * get role list for current tenant
   */
  getRoleList() {
    return fetcher.getRoleList(this.tenantId).then(
      action(list => {
        this.roleList.readRoleList(list);

        if (this.roleList.isEmpty) {
          return this.addRole();
        }
        return this.selectRole(head(list));
      })
    );
  }

  /**
   * Get role templates
   */
  @action
  getRoleTemplates() {
    return fetcher
      .getRoleTemplates(uiState.tenantId)
      .then(roleTemplates => {
        this.roleTemplateList = map(roleTemplate => {
          roleTemplate.name = localizer.getText(`ROLE_SETUP_ROLE_DETAIL_LABEL_ROLE_TYPE_${roleTemplate.id.toUpperCase()}`);
          return roleTemplate;
        }, roleTemplates);
      });
  }

  /**
   * add new role
   */
  @action
  addRole() {
    this.cancelLoadDataModelAccess();

    return this.loadTenantDetail().then(
      action(tenantDetail => {
        this.updateTenantDetail(tenantDetail);

        this.roleDetail.resetData(this.tenantId, this.permissionStore.tenantDetail);
        // load new user list
        this.loadUserList();

        this.dataModelAccess.reset();
        this.permissionStore.setRoleDetailId(null);
        this.permissionStore.reset();
        invoke('unselectRole', this.roleList.configRole);

        // update access model and load data for access grid
        this.accessStore.updateModel(this.roleDetail.permissionAccessModel);
        this.accessStore.initialAccessGrid(this.roleDetail.permission.access.accessDefaults.value);
        this.roleDetail.changeRole();

        // Set default role template
        const authorization = new SecurityManager();
        const canGrantFullReportDashboardAccess = authorization.checkPrivileges([
          PERMISSION_KEY.RoleSetupGrantRoleWithFullReportAndDashboardAccessValue
        ]);

        canGrantFullReportDashboardAccess
          ? this.setRoleTemplate(ROLE_TYPES.Administrator)
          : this.setRoleTemplate(ROLE_TYPES.AdvReportCreator);
        return true;
      })
    );
  }

  @action
  setRoleTemplate(roleType: string) {
    this.roleDetail.setRoleType(roleType);
    if (this.roleDetail.isTemplateRole) {
      const roleTemplate = find({ id: roleType }, this.roleTemplateList);
      if (roleTemplate) {
        this.roleDetail.populatePermissionTemplate(roleTemplate);
        this.permissionStore.updateReportAccessibilityCategory();
        this.permissionStore.updateDashboardAccessibilityCategory();
      }
    }
  }

  /**
   * select role for configRole
   * @param {any} roleListDetail
   * @return {Promise}  Promise to get new data from api for this role
   */
  @action
  selectRole(roleListDetail: any) {
    this.cancelLoadDataModelAccess();

    return Promise.join(
      fetcher.loadRole(roleListDetail.id),
      this.loadTenantDetail(),
      action((roleDetail, tenantDetail) => {
        this.permissionStore.reset();
        this.permissionStore.setRoleDetailId(roleListDetail.id);
        this.permissionSummaryStore.model.updateRoleSelected(roleListDetail.id);

        this.updateTenantDetail(tenantDetail);
        this.roleList.configRole.unselectRole();
        this.roleList.selectRole(roleListDetail.id);

        this.resetAlertMessage();
        this.dataModelAccess.reset();

        // load access
        this.accessStore.updateModel(this.roleDetail.permissionAccessModel);
        this.roleDetail.populateData(roleDetail);
        this.roleDetail.changeRole();

        this.accessStore.initialAccessGrid(roleDetail.permission.access.accessDefaults.value);
        // Load user list
        this.loadUserList();

        this.dataModelAccess.showCheckedQuerySource = roleDetail.showCheckedQuerySource;
        return true;
      })
    );
  }

  /**
   * Build load tenant detail promises.
   */
  loadTenantDetail() {
    return isNil(this.tenantId) ? Promise.resolve(null) : tenantManagementDataFetcher.loadTenant(this.tenantId);
  }

  /**
   * Update tenant detail
   * @param {any} tenantDetail
   */
  updateTenantDetail(tenantDetail: any) {
    if (!tenantDetail) {
      return;
    }
    const permission = get('permission', tenantDetail);
    const permissionDetail = new PermissionDetail(permission);
    this.permissionStore.updateTenantDetail(permissionDetail);
  }

  /**
   * delete role
   * @param {any} role
   * @return {Promise} Promise for calling delete api
   */
  @action
  deleteRole(role: any) {
    return fetcher.deleteRole(role.id).then(() => {
      role.delete();
      if (this.roleList.isEmpty) {
        return this.addRole();
      } else if (role.id === this.roleDetail.id) {
        return this.selectRole(this.roleList.findNextRole(role));
      }
      return null;
    });
  }

  /**
   * toggle active status
   * @param {any} role
   * @return {Promise} Promise for change active status
   */
  @action
  toggleActive(role: any) {
    const api = role.active ? fetcher.deactivateRole : fetcher.activateRole;
    const tenantId = uiState.getTenantId();
    return api(role.id, tenantId).then(result => {
      if (result.success) {
        role.toggleActive();
        role.id === this.roleDetail.id && this.roleDetail.toggleActive();
      }
      return result;
    });
  }

  /**
   * update alert message
   * @method updateAlertMessage
   * @param  {string}           type
   * @param  {string}           message
   */
  updateAlertMessage(type: string, message: string) {
    this.alertMessage = {
      type: type,
      message: message
    };
  }

  /**
   * reset alert message
   */
  resetAlertMessage() {
    this.alertMessage = {};
  }

  /**
   * Load users
   */
  @action
  loadUserList() {
    this.usersRoleSetupStore.model.reload(this.roleDetail.users);
    return Promise.resolve(null);
  }

  /**
   * Load tenant detail
   * @return {Promise} Promise
   */
  @action
  loadTenant() {
    if (!isNil(this.tenantId)) {
      return tenantManagementDataFetcher
        .loadTenant(this.tenantId)
        .then(data => new PermissionDetail(data.permission))
        .then(permissionDetail => (this.permissionStore.tenantDetail = permissionDetail));
    } else {
      this.permissionStore.tenantDetail = null;
    }

    return Promise.resolve(true);
  }

  /**
   * save roleInfo
   * @return {Promise} Promise api
   */
  @action
  async save() {
    // Get update access
    const updatedAccess = this.roleDetail.permissionAccessModel.getAccessForSave();
    // Update list access default
    this.roleDetail.permission.updateAccessDefault(updatedAccess);

    const saveData = this.roleDetail.prepareDataForSave();
    saveData.users = this.usersRoleSetupStore.model.selectedUsers;
    saveData.visibleQuerySourcesTree = this.dataModelAccess.visibleQuerySourcesTree;
    saveData.showCheckedQuerySource = this.dataModelAccess.showCheckedQuerySource;

    const result = await fetcher.saveRole(saveData);
    const messages = this.processResponseSaveRoleSetup(result);

    if (saveData.showCheckedQuerySource) {
      await this.getConnectionDetailInfo(null, true);
    }

    return messages;
  }

  /**
   * Process response save role setup
   * @param  {Object} result
   * @return {Array}
   */
  @action
  processResponseSaveRoleSetup(result: any): Array {
    if (!result.messages) {
      const savedData = result.role;
      const isNew = this.roleDetail.isNew;
      this.dataModelAccess.resetCacheTree();

      if (isNew) {
        this.roleList.addRole(savedData);
        this.permissionStore.setRoleDetailId(savedData.id);
      } else {
        this.roleList.configRole.readBasicInfo(savedData);
      }

      this.roleDetail.populateData(savedData);
      this.accessStore.updateAccessOrigin(savedData.permission.access.accessDefaults.value);

      this.updateAlertMessage(
        commonMessages.MESSAGE_TYPE.SUCCESS,
        localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED')
      );
      // save success then clean dirty
      this.cleanDirty();

      // reload access grid
      return this.accessStore.loadAllAccess(savedData.permission.access.accessDefaults.value);
    }

    return result.messages;
  }

  /**
   * clean Dirty
   */
  @action
  cleanDirty() {
    this.roleDetail.resetDirty();
    this.roleDetail.permission.resetDirty();
    this.roleDetail.permissionAccessModel.resetDirty();
    this.usersRoleSetupStore.model.cleanDirty();
    this.permissionStore.model.resetDirty();
  }

  /**
   * cancel role
   * @return {Promise} Promise api
   */
  @action
  cancel() {
    if (get('isNew', this.roleDetail)) {
      return this.addRole();
    }
    return this.selectRole(this.roleDetail);
  }

  /**
   * load Permission Summary
   * @return {Promise}
   */
  @action
  loadPermissionSummary() {
    this.permissionSummaryStore.model.permissionSummary = [];
    return this.permissionSummaryStore.loadPermissionSummary(this.tenantId);
  }

  /**
   * Save Role Setup
   * @return {promise}
   */
  saveRoleSetup() {
    return fetcher.saveRole(this.roleDetail);
  }

  /**
   * Validation for access
   * @return {Promise} result of validation
   */
  validateAccesses() {
    const updatedAccess = this.roleDetail.permissionAccessModel.getAccessForSave(true);

    var needAPICall = !isEmpty(updatedAccess);

    return needAPICall ? accessFetcher.validateAccesses(updatedAccess) : Promise.resolve(true);
  }

  /**
   * Cancel promise load data model access tree when loading
   */
  cancelLoadDataModelAccess() {
    if (this.loadPartialDataModelAccess && this.loadPartialDataModelAccess.isPending()) {
      this.loadPartialDataModelAccess.cancel();
    }
  }

  /**
   * Get connection detail info
   * @return {Promise}
   */
  @action
  getConnectionDetailInfo(pageInfo: any, isReset: boolean, searchKey: string): Promise {
    const { visibleQuerySourcesTree, filterText, showCheckedQuerySource } = this.dataModelAccess;
    pageInfo = pageInfo || this.lazyLoadingStore.pageInfoForFirstTime;
    searchKey = searchKey || filterText;

    const params = {
      tenantId: this.tenantId,
      roleId: this.roleDetail.id,
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      parentIds: pageInfo.parentIds,
      visibleQuerySourcesTree: visibleQuerySourcesTree,
      criteria: [
        {
          key: 'DataSourceName',
          value: searchKey
        },
        {
          key: 'ShowCheckedQuerySource',
          value: showCheckedQuerySource
        }
      ]
    };

    this.loadPartialDataModelAccess = fetcher.loadPartialDataModelAccess(params);

    return this.loadPartialDataModelAccess.then(data => {
      this.processDataModelAccess(isReset, searchKey, data);
      this.loadPartialDataModelAccess = null;
      return data;
    });
  }

  /**
   * Process lazy loading data model access
   * @param  {Boolean} isReset
   * @param  {String} searchKey
   * @param  {Object} response
   */
  @action
  processDataModelAccess(isReset: boolean, searchKey: string, response: any) {
    const dataModelAccess = this.dataModelAccess;
    dataModelAccess.isLastPage = response.isLastPage;
    isReset && dataModelAccess.resetData();

    if (toString(searchKey) !== toString(this.dataModelAccess.filterText)) {
      return;
    }

    const newData = dataModelAccess.convertData(response.data);
    concatTree(dataModelAccess.connectionList, newData, DATA_SOURCE_CONCAT_TREE_MAP, response.numOfChilds, []);
    updateTree(dataModelAccess.connectionList, this.dataModelAccess.visibleQuerySourcesTree, DATA_SOURCE_TREE_MAP, [
      'checked',
      'interacted',
      'indeterminate',
      'numOfCheckedChilds'
    ]);
    dataModelAccess.displayData = parseToTree(dataModelAccess.connectionList, DATA_SOURCE_TREE_MAP);
  }

  /**
   * Search connection details
   * @param  {String} searchKey
   * @return {Promise}
   */
  @action
  searchDataSourceItems(searchKey: string) {
    this.getConnectionDetailInfo(null, true, searchKey);
  }

  /**
   * Register LazyLoading Store
   * @param  {Object} store
   */
  registerLazyLoadingStore(store: any) {
    this.lazyLoadingStore = store;
  }

  /**
   * Handle change data source item
   * @param  {Object} node
   * @param  {Object} parent
   */
  @action
  handleChangeDataSourceItems(node: any, parent: any) {
    if (this.dataModelAccess.showCheckedQuerySource) {
      !node.source.checked && (node.source.uncheckedSSO = true);
    }
    updateInteractiveTree(node, parent, this.dataModelAccess.visibleQuerySourcesTree);
  }

  /**
   * Handle toggle show query source
   * @param  {boolean} checked
   */
  @action
  handleToggleShowSelectedQuerySource(checked: boolean): Promise {
    // cancel load data model access if it loading
    this.cancelLoadDataModelAccess();
    // set flag showCheckedQuerySource
    this.dataModelAccess.setShowCheckedQuerySource(checked);
    // reset flag uncheckedSSO
    this.resetUncheckSSO(this.dataModelAccess.visibleQuerySourcesTree);

    // reload data model access
    return this.getConnectionDetailInfo(null, true);
  }

  /**
   * Reset UncheckSSO
   * @param  {Array} tree
   */
  resetUncheckSSO(tree: Array) {
    forEach(item => {
      item.uncheckedSSO = false;
      this.resetUncheckSSO(item.childNodes);
    }, tree);
  }
}

export default RoleSetupStore;



// WEBPACK FOOTER //
// ./ui/logic/Izenda.RoleSetupStore.js